import React from 'react';
import { Tooltip } from 'antd';
import { CloudOutlined } from '@ant-design/icons';

import { PublisherExpandedCard } from './PublisherExpandedCard';
import { formatWholeNumber } from '../../../../../../../core/utils/campaigns';
import DataTable from '../../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// PublisherCTRTable — Display tab's Publishers table, on shared <DataTable>.
//
// Differences from the Creatives table:
//   - No tabs (all/active/inactive) — publishers don't have activity state
//   - No Click Through Link column — publishers don't carry a clickable target
//   - Name column decodes HTML entities and relabels bad data as "Unknown
//     publisher" (the backend sometimes dumps the Creative's own label into
//     the publisher slot; v1 shipped the same fallback)
//   - Archive bucket 'z_archive Deprecated DSP Publishers' always sorts last,
//     regardless of column direction — handled in PublisherDetails.js as a
//     pre-sort on the data. Column sorters ignore direction for that row.
// =============================================================================

const ARCHIVED_LABEL = 'z_archive Deprecated DSP Publishers';

// Keeps the archive bucket pinned to the bottom of any sort order.
const stableSort = (columnSort) => (rowA, rowB, columnId) => {
  const a = rowA.original;
  const b = rowB.original;
  if (a.value === ARCHIVED_LABEL && b.value === ARCHIVED_LABEL) return 0;
  if (a.value === ARCHIVED_LABEL) return 1;
  if (b.value === ARCHIVED_LABEL) return -1;
  return columnSort(rowA, rowB, columnId);
};

const numericSort = (key) => (rowA, rowB) => {
  return (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);
};

const textSort = (rowA, rowB) => {
  const a = rowA.original.value || '';
  const b = rowB.original.value || '';
  return a.localeCompare(b);
};

const decodeHtml = (text) => {
  if (!text) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    return doc.body.textContent || '';
  } catch (_) {
    return text;
  }
};

const renderPublisherName = (rawValue) => {
  if (!rawValue) return <div />;
  const decoded = decodeHtml(rawValue);
  // When the backend misdirects a creative name into the publisher slot the
  // string contains 'Creative' — relabel as "Unknown publisher" instead of
  // leaking the misplaced creative name. Parity with v1.
  const pubUnknown = decoded.includes('Creative');
  const display = pubUnknown
    ? 'Unknown publisher'
    : decoded.length > 45
      ? `${decoded.slice(0, 46)}…`
      : decoded;
  const needsTooltip = !pubUnknown && decoded.length > 45;
  if (needsTooltip) {
    return (
      <Tooltip overlayClassName="mjl-ds" title={decoded}>
        <span className="campaign-overview__creatives-name">{display}</span>
      </Tooltip>
    );
  }
  return <span className="campaign-overview__creatives-name">{display}</span>;
};

const PublisherCTRTable = ({ publisherData }) => {
  // Filter out the synthetic '[tail aggregate]' row; v1 did this in the
  // SimpleTable data= prop.
  const rows = (publisherData || []).filter(
    (publisher) => publisher.xAxis !== '[tail aggregate]'
  );

  // Only render the thumbnail column if at least one publisher has an image —
  // otherwise it's a wasted empty column.
  const hasThumbnailColumn = rows.some((p) => p.imageUrl);

  const columns = [];
  columns.push({
    id: 'color',
    accessorKey: 'color',
    header: '',
    sortable: false,
    meta: { align: 'center', width: 24 },
    cell: ({ getValue }) => (
      <span
        className="campaign-overview__creatives-dot"
        style={{ backgroundColor: getValue() }}
        aria-hidden="true"
      />
    ),
  });

  if (hasThumbnailColumn) {
    columns.push({
      id: 'thumbnail',
      accessorKey: 'imageUrl',
      header: '',
      sortable: false,
      meta: { align: 'left', width: 60 },
      cell: ({ getValue }) => {
        const url = getValue();
        const hasImage = url && url !== '';
        return (
          <div
            className="campaign-overview__creatives-thumb"
            style={hasImage ? { backgroundImage: `url("${url}")` } : undefined}
          >
            {!hasImage && <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />}
          </div>
        );
      },
    });
  }

  columns.push({
    id: 'value',
    accessorKey: 'value',
    header: 'Name',
    sortable: true,
    sortingFn: stableSort(textSort),
    meta: { align: 'left', width: 280 },
    cell: ({ getValue }) => renderPublisherName(getValue()),
  });

  columns.push({
    id: 'impressions',
    accessorKey: 'impressions',
    header: 'Impressions',
    sortable: true,
    sortingFn: stableSort(numericSort('impressions')),
    meta: { align: 'right', width: 140 },
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatWholeNumber(v) : '-';
    },
  });

  columns.push({
    id: 'clicks',
    accessorKey: 'clicks',
    header: 'Clicks',
    sortable: true,
    sortingFn: stableSort(numericSort('clicks')),
    meta: { align: 'right', width: 110 },
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatWholeNumber(v) : '-';
    },
  });

  columns.push({
    id: 'ctr',
    accessorKey: 'ctr',
    header: 'CTR',
    sortable: true,
    sortingFn: stableSort(numericSort('ctr')),
    meta: { align: 'right', width: 100 },
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? `${v.toFixed(3)}%` : '-';
    },
  });

  const totalImpressions = rows.reduce(
    (sum, d) => sum + (parseFloat(d.impressions) || 0),
    0
  );
  const totalClicks = rows.reduce(
    (sum, d) => sum + (parseFloat(d.clicks) || 0),
    0
  );

  // Mobile card — thumb/dot/name header + 3 metric tiles. No link since
  // publishers don't carry a clickable target.
  const renderMobileCard = (record) => {
    const hasThumb = record.imageUrl && record.imageUrl !== '';
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          {hasThumb ? (
            <div
              className="campaign-overview__creatives-thumb"
              style={{ backgroundImage: `url("${record.imageUrl}")` }}
            />
          ) : (
            <div className="campaign-overview__creatives-thumb">
              <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />
            </div>
          )}
          <div className="campaign-overview__creatives-card-name">
            <span
              className="campaign-overview__creatives-dot"
              style={{ backgroundColor: record.color }}
              aria-hidden="true"
            />
            {renderPublisherName(record.value)}
          </div>
        </div>

        <div className="campaign-overview__creatives-card-metrics">
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.impressions ? formatWholeNumber(record.impressions) : '-'}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Clicks</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.clicks ? formatWholeNumber(record.clicks) : '-'}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">CTR</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.ctr ? `${record.ctr.toFixed(3)}%` : '-'}
            </span>
          </div>
        </div>
      </>
    );
  };

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      expandable={{
        isRowExpandable: (record) =>
          !!record.extra ||
          (record.media && record.media.type) === 'THREEDEE',
        renderExpandedRow: (record) =>
          record && <PublisherExpandedCard record={record} />,
      }}
      summaryRow={{
        labelColumnId: 'value',
        label: 'Totals + Averages',
        cells: {
          impressions: formatWholeNumber(totalImpressions),
          clicks: formatWholeNumber(totalClicks),
          ctr: '-',
        },
      }}
      emptyState="No publishers match the current filter."
    />
  );
};

export default PublisherCTRTable;
